import { computed, DeepReadonly } from 'vue';
import { useI18n } from 'vue-i18n';
import { Transcription } from '@/api/openapi';

type TSpeakerType = 'receptionist' | 'insideSales' | 'customer' | 'unknown';

export const SPEAKER_COLORS = {
  'receptionist': '#969696',
  'insideSales': '#2CBFB7',
  'customer': '#9EA7DF',
  'unknown': '#E3E3E3',
};

const useTranscriptionSpeakers = (getTranscription: () => DeepReadonly<Transcription> | null) => {
  const { t } = useI18n();

  const SPEAKER_LABEL = {
    'insideSales': t('transcription.speakerLabel.insideSales'),
  };

  const speakerPresumption = computed(() => {
    return getTranscription()?.complementedSpeakerPresumption;
  });

  const computeColorBySpeaker = (speaker: string | null): string => {
    const speakerType = computeSpeakerType(speaker);
    return SPEAKER_COLORS[speakerType];
  };

  const computeSpeakerType = (speaker: string | null): TSpeakerType => {
    if (speaker === speakerPresumption.value?.receptionist) {
      return 'receptionist';
    }
    if (speaker === speakerPresumption.value?.insideSales) {
      return 'insideSales';
    }
    if (speaker === speakerPresumption.value?.customer) {
      return 'customer';
    }
    return 'unknown';
  };

  const findInsideSalesSpeaker = () => {
    return speakerPresumption.value?.insideSales;
  };

  const findCustomerSpeaker = () => {
    return speakerPresumption.value?.customer;
  };

  const computeSpeakerName = (speaker: string) => {
    if (speaker == null) {
      return `話者${speakerNumberMap.value.size + 1}`;
    }

    const speakerType = computeSpeakerType(speaker);
    if (speakerType === 'insideSales') {
      return SPEAKER_LABEL[speakerType];
    }

    const speakerNumber = speakerNumberMap.value.get(speaker);
    if (speakerNumber != null) {
      return `話者${speakerNumber}`;
    }

    return speaker;
  };

  const allSpeakers = computed(() => {
    return getTranscription()?.mergedTexts?.map((text) => text.speaker) ?? [];
  });

  const parseSpeakerIndex = (speaker: string): number | null => {
    const speakerMatch = /^SPEAKER_(\d+)$/.exec(speaker);
    return speakerMatch ? parseInt(speakerMatch[1], 10) : null;
  };

  const speakerNumberMap = computed(() => {
    // NOTE: 以下より、 `speakerPresumption` からでなく、実際のすべての話者から算出する。
    //       - `speakerPresumption（complementedSpeakerPresumption）` にすべての話者が含まれているとは限らない。
    // NOTE: `SPEAKER_NN` の数値が通し番号でない場合を一応考慮して、番号を振り直す際は `SPEAKER_NN` の数値部分の昇順で並び替える。
    // NOTE: `insideSales`（架電者）は固定ラベルで表示するため、番号付けの対象から除外する。
    const insideSalesSpeaker = findInsideSalesSpeaker();
    const numberedSpeakers = Array.from(new Set(allSpeakers.value))
      .filter((speaker): speaker is string => speaker != null && speaker !== insideSalesSpeaker && parseSpeakerIndex(speaker) != null)
      .sort((a, b) => (parseSpeakerIndex(a) ?? 0) - (parseSpeakerIndex(b) ?? 0));

    return new Map(numberedSpeakers.map((speaker, index) => [speaker, index + 1]));
  });

  return {
    computeColorBySpeaker,
    findInsideSalesSpeaker,
    findCustomerSpeaker,
    computeSpeakerName,
  };
};

export { useTranscriptionSpeakers };
